import React, { useContext, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getProducts } from '../../clients/product_client';
import { listStorageNodeContents } from '../../clients/storage_client';
import S3MediaImage from '../Generic/S3MediaImage';
import ProductDetailModal from '../Product/ProductDetailModal';
import { defaultImageUrl } from '../../config_and_helpers/config';
import { logError, logInfo } from '../../config_and_helpers/logger';
import { readSlotContentDisplay } from '../../utils/storagePlace';
import StorageLotMoveBar from './StorageLotMoveBar';
import {
  boxSlotColumns,
  buildBoxSlots,
  readSlotCount,
} from '../../utils/storageTree';

const LOG_CONTEXT = 'StorageBoxChart';

/**
 * Inventory product whose CAT# matches, so a thin contents row can show an image
 * and open the inventory modal.
 *
 * @param {string} token
 * @param {string} catNum
 * @returns {Promise<Object|null>}
 */
function lookupProductByCatNum(token, catNum) {
  return getProducts(token, () => {}, {
    searchInput: catNum,
    supplierCatalogueFilter: 'false',
  }).then(response => {
    const results = response?.success && Array.isArray(response.results) ? response.results : [];
    const match = results.find(
      product => String(product?.cat_num || '').trim() === String(catNum).trim()
    );
    return match || null;
  });
}

/**
 * Slot chart for one slotted box. A filled cell shows the product image, CAT#,
 * and lot. Clicking it opens that product's inventory modal. An empty cell can
 * Place a lot into that slot.
 *
 * Rendered from the page (not inside the React Flow node) so the map's
 * overflow does not clip it.
 *
 * @param {Object} props
 * @param {import('../../utils/storageTree').StorageNode|null} props.box
 * @param {Function} props.onClose
 * @param {Array} [props.nodes] - Lab storage tree, for the move picker
 * @param {Function} [props.onPlaceInSlot] - (slotIndex) => void. Opens the inventory search for that slot
 * @param {number} [props.refreshSignal] - Bump after a lot is placed so contents reload
 * @param {Function} [props.onLotsChanged] - Called after a lot is moved so card counts reload
 * @param {Function} [props.contentsLoader] - ({ reload }) => Promise. When set, the page owns the request
 */
const StorageBoxChart = ({
  box,
  nodes = [],
  onClose,
  onPlaceInSlot,
  refreshSignal = 0,
  onLotsChanged,
  contentsLoader = null,
}) => {
  const app = useContext(AppContext);
  const isViewer = useIsLabViewer();
  const canPlaceInSlot = typeof onPlaceInSlot === 'function' && !isViewer;
  const token = app?.token;
  const isSupplier = Boolean(app?.isSupplier);
  const slotCount = readSlotCount(box);
  const slots = buildBoxSlots(slotCount || 0);
  const columns = boxSlotColumns(slotCount || 0);
  const [contents, setContents] = useState([]);
  const [productsByCat, setProductsByCat] = useState({});
  const [openProduct, setOpenProduct] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [localReload, setLocalReload] = useState(0);
  const seenRefresh = useRef(refreshSignal);
  const seenLocalReload = useRef(localReload);
  const boxId = box?.id ?? null;

  useEffect(() => {
    setContents([]);
    setSelectedIds([]);
  }, [boxId]);

  useEffect(() => {
    if (boxId == null || slotCount == null || !token) {
      setContents([]);
      return undefined;
    }
    let active = true;
    if (contentsLoader) {
      const reload =
        refreshSignal !== seenRefresh.current || localReload !== seenLocalReload.current;
      seenRefresh.current = refreshSignal;
      seenLocalReload.current = localReload;
      contentsLoader({ reload }).then(result => {
        if (!active || !result || result.cancelled) return;
        if (result.error) {
          logError(LOG_CONTEXT, 'box contents failed', { boxId });
          return;
        }
        const items = (result.items || []).map(item => ({ ...item, boxId }));
        setContents(items);
        logInfo(LOG_CONTEXT, 'box contents loaded', {
          boxId,
          itemCount: items.length,
          shared: true,
          refreshSignal,
        });
      });
      return () => {
        active = false;
      };
    }
    const controller = new AbortController();
    listStorageNodeContents(token, boxId, { signal: controller.signal }).then(result => {
      if (!active || result.cancelled) return;
      if (!result.success) {
        logError(LOG_CONTEXT, 'box contents failed', {
          boxId,
          status: result.status ?? null,
        });
        return;
      }
      const items = (result.data || []).map(item => ({ ...item, boxId }));
      setContents(items);
      logInfo(LOG_CONTEXT, 'box contents loaded', {
        boxId,
        itemCount: items.length,
        status: result.status ?? null,
        notDeployed: Boolean(result.notDeployed),
        refreshSignal,
      });
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [boxId, slotCount, token, refreshSignal, localReload, contentsLoader]);

  useEffect(() => {
    if (!token) {
      setProductsByCat({});
      return undefined;
    }
    const missing = [
      ...new Set(
        contents
          .map(item => readSlotContentDisplay(item))
          .filter(display => display.catNum && (display.productId == null || !display.imageUrl))
          .map(display => display.catNum)
      ),
    ];
    if (!missing.length) {
      setProductsByCat({});
      return undefined;
    }
    let active = true;
    logInfo(LOG_CONTEXT, 'look up products for chart cells', { catNums: missing });
    Promise.all(
      missing.map(catNum =>
        lookupProductByCatNum(token, catNum)
          .then(product => [catNum, product])
          .catch(error => {
            logError(LOG_CONTEXT, 'chart product lookup failed', { catNum, error });
            return [catNum, null];
          })
      )
    ).then(pairs => {
      if (!active) return;
      setProductsByCat(Object.fromEntries(pairs));
      logInfo(LOG_CONTEXT, 'chart product lookup finished', {
        found: pairs.filter(([, product]) => product).length,
        requested: pairs.length,
      });
    });
    return () => {
      active = false;
    };
  }, [contents, token]);

  const bySlot = new Map(
    contents
      .filter(item => item && item.slot_index != null)
      .map(item => [Number(item.slot_index), item])
  );

  return (
    <>
      <Modal
        show={Boolean(box) && slotCount != null}
        onHide={onClose}
        size="xl"
        centered
        enforceFocus={false}
        className="storage-box-chart-modal"
        aria-labelledby="storage-box-chart-title"
      >
        <Modal.Header closeButton>
          <Modal.Title as="h2" id="storage-box-chart-title">{box?.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {slotCount} {slotCount === 1 ? 'slot' : 'slots'}. Click a product name
            to open its inventory.
            {isViewer ? '' : ' Select lots to move them.'}
          </p>
          {!isViewer ? (
          <StorageLotMoveBar
            token={token}
            nodes={nodes}
            lots={contents.filter(item => selectedIds.includes(String(item.id)))}
            onMoved={({ failed, moved, storageNodeId }) => {
              setSelectedIds((failed || []).map(lot => String(lot.id)));
              setLocalReload(key => key + 1);
              onLotsChanged?.({ moved, storageNodeId });
            }}
          />
          ) : null}
          <div
            className="storage-box-chart"
            role="grid"
            aria-label={`${box?.name || 'Box'} slots`}
            style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
          >
            {slots.map(slot => {
              const item = bySlot.get(slot.index);
              if (!item) {
                return (
                  <div
                    key={slot.index}
                    className="storage-box-slot storage-box-slot--empty"
                    role="gridcell"
                    aria-label={`Slot ${slot.index}, empty`}
                  >
                    <span className="storage-box-slot__index">{slot.index}</span>
                    {canPlaceInSlot ? (
                    <button
                      type="button"
                      className="storage-box-slot__place"
                      aria-label={`Place in slot ${slot.index}`}
                      onClick={() => onPlaceInSlot(slot.index)}
                    >
                      <svg
                        className="storage-box-slot__place-icon"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                      >
                        <path
                          d="M24 6v10M19 11h10"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                        />
                        <path
                          d="M24 14v6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                        />
                        <path
                          d="M8 22h32l-3 16H11L8 22z"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M8 22l6-6h20l6 6"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </button>
                    ) : null}
                  </div>
                );
              }
              const preview = readSlotContentDisplay(item);
              const display = readSlotContentDisplay(item, productsByCat[preview.catNum]);
              const imageUrl = display.imageUrl || defaultImageUrl;
              const lotLine = item?.batch ? `Lot ${display.lot}` : display.lot;
              const showCat = display.catNum && display.catNum !== display.name;
              const openLabel = `Open ${display.name}`;
              const cellLabel = `Slot ${slot.index}, ${display.name}${showCat ? `, ${display.catNum}` : ''}, lot ${display.lot}`;
              const photo = (
                <S3MediaImage
                  url={imageUrl}
                  alt={display.name || display.catNum || 'Product'}
                  className="storage-box-slot__photo"
                  fallbackUrl={defaultImageUrl}
                />
              );
              const nameControl = display.productId != null ? (
                <button
                  type="button"
                  className="storage-box-slot__name"
                  aria-label={openLabel}
                  onClick={() => {
                    logInfo(LOG_CONTEXT, 'open inventory from chart cell', {
                      productId: display.productId,
                      catNum: display.catNum,
                      slotIndex: slot.index,
                    });
                    setOpenProduct({
                      id: display.productId,
                      cat_num: display.catNum,
                      name: display.name,
                      first_image: display.imageUrl || undefined,
                    });
                  }}
                >
                  {display.name}
                </button>
              ) : (
                <span className="storage-box-slot__name">{display.name}</span>
              );
              const caption = (
                <span className="storage-box-slot__caption">
                  {nameControl}
                  {showCat ? (
                    <span className="storage-box-slot__cat">{display.catNum}</span>
                  ) : null}
                  <span className="storage-box-slot__lot">{lotLine}</span>
                </span>
              );
              return (
                <div
                  key={slot.index}
                  className="storage-box-slot storage-box-slot--filled"
                  role="gridcell"
                  aria-label={cellLabel}
                >
                  <span className="storage-box-slot__index">{slot.index}</span>
                  {!isViewer ? (
                  <input
                    type="checkbox"
                    className="storage-lot-select storage-lot-select--slot"
                    checked={selectedIds.includes(String(item.id))}
                    aria-label={`Select ${display.catNum || display.name}, ${lotLine}`}
                    onChange={() => {
                      const id = String(item.id);
                      setSelectedIds(current =>
                        current.includes(id) ? current.filter(value => value !== id) : [...current, id]
                      );
                    }}
                  />
                  ) : null}
                  <div className="storage-box-slot__open storage-box-slot__open--static">
                    {photo}
                    {caption}
                  </div>
                </div>
              );
            })}
          </div>
        </Modal.Body>
      </Modal>
      {openProduct ? (
        <ProductDetailModal
          isSupplier={isSupplier}
          productObj={openProduct}
          productId={openProduct.id}
          hideTrigger
          show
          onHide={() => setOpenProduct(null)}
        />
      ) : null}
    </>
  );
};

export default StorageBoxChart;
